<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { resolveNavigationTarget } from "#bb/lib/navigationTarget";
import {
  isCurrentUserStateTarget,
  resolveUserStateRedirectContext,
  selectUserStateTarget,
  userStatePath,
  type UserStateRecord,
} from "#bb/lib/userState";

const props = defineProps<{ id: string }>();
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});
const features = inject("render-features", {
  editor: false,
  resourceDefinitionPath: null as string | null,
});
const route = useRoute();
const { siteId } = useSite();
const { profile } = useProfile();
const { resolveHref } = useAppRoute();
const logger = useLogger("UserStateRedirect");
let redirectTimeout: ReturnType<typeof setTimeout> | null = null;
let scheduledSignature: string | null = null;
let redirectCompleted = false;
let redirectGeneration = 0;
let invalidLocatorLogged: string | null = null;

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "User state redirect editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the user state key and fallback target",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "User State Redirect",
    icon: "lucide:history",
  },
});

async function loadStateRecord(
  resolvedSiteId: string,
  userId: string,
  dataPath: string[],
  key: string,
): Promise<UserStateRecord | null> {
  const { getEntry } = useFileEntries(resolvedSiteId);
  const entry = await getEntry(userStatePath(userId, dataPath, key), "metadata", {
    suppressNotFoundToast: true,
  });
  if (!entry?.path || !entry.versions?.length) {
    return null;
  }

  const latestVersion = entry.versions.reduce((latest, version) =>
    version.version > latest.version ? version : latest,
  );
  try {
    const raw = await useApiFetch<string>(
      `/v1/file/${resolvedSiteId}/download/${entry.path}?version=${latestVersion.id}`,
      { responseType: "text" },
    );
    return JSON.parse(raw) as UserStateRecord;
  } catch (error) {
    logger.warn("Unable to load user state; using fallback", {
      dataPath,
      key,
      error,
    });
    return null;
  }
}

async function redirectFromState(context: {
  siteId: string;
  userId: string;
  dataPath: string[];
  key: string;
}, generation: number) {
  const record = await loadStateRecord(
    context.siteId,
    context.userId,
    context.dataPath,
    context.key,
  );
  if (generation !== redirectGeneration) return;

  const target = selectUserStateTarget(
    record,
    definitionData.value?.fallbackPath,
  );
  const navigationTarget = resolveNavigationTarget(target, {
    currentPath: route.fullPath,
    resourceDefinitionPath: features.resourceDefinitionPath,
    resolvePath: resolveHref,
  });

  if (!navigationTarget?.href) {
    redirectCompleted = true;
    logger.warn("No valid user state redirect target is available", {
      dataPath: context.dataPath,
      key: context.key,
    });
    return;
  }
  if (
    isCurrentUserStateTarget(
      navigationTarget.href,
      route.fullPath,
      navigationTarget.external,
    )
  ) {
    redirectCompleted = true;
    return;
  }

  redirectCompleted = true;
  await navigateTo(navigationTarget.href, {
    external: navigationTarget.external,
  });
}

const redirectContext = computed(() =>
  resolveUserStateRedirectContext({
    editor: Boolean(features.editor),
    siteId: siteId.value,
    userId: profile.value?.id,
    dataPath: getResolvedDataPath(props.id),
    key: definitionData.value?.stateKey,
  }),
);

watch(
  [
    redirectContext,
    () => definitionData.value?.fallbackPath,
    () => definitionData.value?.delaySeconds,
  ],
  ([context, fallbackPath, configuredDelay]) => {
    if (redirectCompleted || !import.meta.client) return;

    if (!context.ready) {
      redirectGeneration++;
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
        redirectTimeout = null;
        scheduledSignature = null;
      }
      if (
        context.reason === "locator"
      ) {
        const signature = JSON.stringify([
          getResolvedDataPath(props.id),
          definitionData.value?.stateKey,
        ]);
        if (signature !== invalidLocatorLogged) {
          invalidLocatorLogged = signature;
          logger.warn("User state redirect is waiting for a valid data path and key", {
            dataPath: getResolvedDataPath(props.id),
            key: definitionData.value?.stateKey,
          });
        }
      }
      return;
    }

    const delay = Math.max(0, Number(configuredDelay ?? 0));
    const signature = [
      context.siteId,
      context.userId,
      context.dataPath.join("/"),
      context.key,
      fallbackPath ?? "",
      delay,
    ].join("|");
    if (signature === scheduledSignature) return;

    const generation = ++redirectGeneration;
    if (redirectTimeout) clearTimeout(redirectTimeout);
    scheduledSignature = signature;
    logger.debug("User state redirect scheduled", {
      dataPath: context.dataPath,
      key: context.key,
      delaySeconds: delay,
    });

    redirectTimeout = setTimeout(() => {
      redirectTimeout = null;
      scheduledSignature = null;
      void redirectFromState(context, generation).catch((error) => {
        if (generation !== redirectGeneration) return;
        logger.error("User state redirect failed", {
          dataPath: context.dataPath,
          key: context.key,
          error,
        });
      });
    }, delay * 1000);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  redirectGeneration++;
  if (redirectTimeout) {
    clearTimeout(redirectTimeout);
  }
});
</script>

<template />

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.USER_STATE_REDIRECT,
  icon: "lucide:history",
  type: "User State Redirect",
  description:
    "Redirects to a site-local or HTTPS path stored in the current user's state, with a fallback target.",
  editor: () => import("#bb/components/editor/navigation/user-state-redirect.vue"),
  default: {
    stateKey: "assessment.resume",
    fallbackPath: "/",
    delaySeconds: 0,
  },
  categories: ["Navigation"],
  order: 12005,
};
</script>
